import { useState, type ReactNode } from "react";
import { Sparkles } from "lucide-react";
import { cn } from "@/utils/helpers";
import { KitSegmentedControl } from "../../components/kit/SegmentedControl";
import type { PostViewport } from "../components/StatusBanner";
import { clockLabel, compactNumber, dayLabel, metricTotals, PLATFORM_NAME, type MetricKind } from "./liveModel";
import type { LiveData } from "./liveTypes";
import LiveNumbersChart, { type Granularity } from "./LiveNumbersChart";
import { chartPlatforms } from "./LivePostDialog";
import { listWords } from "./LiveParts";

const HOUR = 3_600_000;
const DAY = 24 * HOUR;
const WORD: Record<MetricKind, string> = { views: "views", likes: "likes", comments: "comments", shares: "shares", saves: "saves" };

/** The five numbers' figures (Figma 08): thousands written out ("1,830"), compact from ten thousand ("19.5K"). */
function figure(value: number): string {
  return Math.abs(value) < 10_000 ? Math.round(value).toLocaleString("en-US") : compactNumber(value);
}

/** "14 hours in", "1 hour in", "under an hour in". */
function hoursIn(ms: number): string {
  const hours = Math.floor(ms / HOUR);
  return hours < 1 ? "under an hour in" : `${hours} ${hours === 1 ? "hour" : "hours"} in`;
}

/**
 * Where the numbers are in their first day or week, for the line beside the
 * Numbers / Comments switch and the phone's label (Figma 08): "The first 24
 * hours, 14 hours in. Fri 11 Dec, 08:02." / "The first week, day 2 of 7. …".
 */
function windowWords(granularity: Granularity, firstLive: number, readAt: number, timeZone: string): { line: string; label: string } {
  const iso = new Date(readAt).toISOString();
  const when = `${dayLabel(iso, timeZone)}, ${clockLabel(iso, timeZone)}`;
  const since = readAt - firstLive;
  if (granularity === "hourly") {
    if (since < DAY) return { line: `The first 24 hours, ${hoursIn(since)}. ${when}.`, label: `The first 24 hours · ${hoursIn(since)}` };
    const from = new Date(firstLive).toISOString();
    return { line: `The first 24 hours, from ${dayLabel(from, timeZone)}, ${clockLabel(from, timeZone)}.`, label: "The first 24 hours" };
  }
  const day = Math.floor(since / DAY) + 1;
  if (day <= 7) return { line: `The first week, day ${day} of 7. ${when}.`, label: `The first week · day ${day} of 7` };
  return { line: `The first week. Last read ${when}.`, label: "The first week" };
}

/**
 * The numbers of a live post (Figma 08, 6 Live, "The first 24 hours"), the
 * same for the team and the client on every size: the total views as the
 * hero, Creo's read when there is one, the five numbers (they pick what the
 * graph draws), the graph, and where the numbers come from, under a Numbers
 * / Comments switch when the comments are given. Refresh for whoever may.
 */
export default function LiveNumbers({
  slotId,
  data,
  viewport,
  timeZone,
  comments,
  onRefresh,
}: {
  slotId: string;
  data: LiveData;
  viewport: PostViewport;
  timeZone: string;
  /** The audience's comments, behind the switch. */
  comments?: ReactNode;
  /** Reads every live channel's numbers now (edit roles only: a read can be a paid one). */
  onRefresh?: () => void;
}) {
  const phone = viewport === "phone";
  const live = data.posts.filter((post) => post.liveSince);
  const platforms = chartPlatforms(data);
  const totals = metricTotals(live);
  const firstLive = Math.min(...live.map((post) => new Date(post.liveSince!).getTime()));
  const reads = live.map((post) => post.metricsUpdatedAt).filter((value): value is string => !!value).sort();
  const lastRead = reads.length ? reads[reads.length - 1] : null;
  // The page's clock, taken when it opens (the reads move the words, not a timer).
  const [now] = useState(() => Date.now());
  // Daily is where a post past its first day opens.
  const [granularity, setGranularity] = useState<Granularity>(() => (now - firstLive >= DAY ? "daily" : "hourly"));
  const [tab, setTab] = useState<"numbers" | "comments">("numbers");
  const [picked, setPicked] = useState<MetricKind>("views");
  const kind = totals.some((total) => total.kind === picked) ? picked : (totals[0]?.kind ?? "views");
  const words = windowWords(granularity, firstLive, lastRead ? new Date(lastRead).getTime() : now, timeZone);

  // The hero: total views (else the first number there is), across the platforms that gave it.
  const heroKind = totals.find((total) => total.kind === "views") ?? totals[0];
  const heroPlatforms = heroKind
    ? [
        ...new Set(
          live
            .filter((post) => typeof post.metrics?.[heroKind.kind] === "number")
            .map((post) => {
              const platform = data.channels.find((channel) => channel.id === post.channelId)?.platform ?? "";
              return PLATFORM_NAME[platform] ?? platform;
            }),
        ),
      ]
    : [];

  const results = data.results;
  const reading = live.some((post) => post.tracking?.reading);
  const sources = new Set(live.filter((post) => post.metricsUpdatedAt).map((post) => post.metricsSource));
  const connected = live.filter((post) => post.metricsSource === "CONNECTED").length;
  const source =
    sources.has("CONNECTED") && sources.has("PUBLIC")
      ? "From the connected accounts and public numbers"
      : sources.has("CONNECTED")
        ? connected > 1
          ? "From the connected accounts"
          : "From the connected account"
        : sources.has("PUBLIC")
          ? "From the public numbers"
          : null;
  const updated = lastRead ? `Updated ${clockLabel(lastRead, timeZone, now - new Date(lastRead).getTime() > 20 * HOUR)}` : null;

  const block = (
    <div className="flex min-w-0 flex-col gap-5">
      {heroKind ? (
        <div className={cn("flex", phone ? "flex-col gap-1.5" : "items-start gap-4")}>
          {/* 62.4 on the phone, 76.8 in a narrow column (Figma 885:110343), 96 across a wide one (239:71385). */}
          <p
            className={cn(
              "min-w-[3.125em] font-display font-bold tracking-[-0.02em] text-co-text",
              // Line height rides on the size. A separate leading-* is dropped by twMerge against text-[…], so the figure collapses to a 24px line.
              phone ? "text-[62.4px]/[0.9]" : "text-[76.8px]/[0.9] @min-[1100px]:text-[96px]/[0.9]",
            )}
          >
            {compactNumber(heroKind.value)}
          </p>
          <p className={cn("text-[15px] font-semibold leading-[20.5px] text-co-text", !phone && "pt-3.5")}>
            {WORD[heroKind.kind]} {heroPlatforms.length > 1 ? "across" : "on"} {listWords(heroPlatforms)}
          </p>
        </div>
      ) : (
        <p className="text-[13px] text-co-muted">The numbers come in with the first read, within the hour.</p>
      )}

      {results ? (
        <div className="flex flex-col gap-1.5">
          <p className="flex items-center gap-2 text-[12px] leading-[16.8px] text-co-muted">
            <Sparkles className="size-[15px] text-co-accent" strokeWidth={1.8} aria-hidden="true" />
            Creo&apos;s read
            <span>{[results.window, results.sampleLabel].filter(Boolean).join(" · ")}</span>
          </p>
          <p className="text-[14px] leading-[21px] text-co-text">{results.read}</p>
        </div>
      ) : null}

      {totals.length ? (
        <div role="radiogroup" aria-label="The number the graph shows" className={cn("flex flex-wrap gap-y-3", phone ? "gap-x-7" : "gap-x-11")}>
          {totals.map((total) => {
            const on = total.kind === kind;
            return (
              <button
                key={total.kind}
                type="button"
                role="radio"
                aria-checked={on}
                onClick={() => setPicked(total.kind)}
                className="relative flex flex-col gap-0.5 pb-1.5 text-left outline-none focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-co-accent"
              >
                <span className={cn("font-section text-[30px] font-semibold leading-[30px]", on ? "text-co-text" : "text-co-text2")}>
                  {figure(total.value)}
                </span>
                <span className="text-[12px] leading-[16.4px] text-co-muted">{WORD[total.kind]}</span>
                {on ? <span aria-hidden="true" className="absolute inset-x-0 bottom-0 h-0.5 bg-co-accent" /> : null}
              </button>
            );
          })}
        </div>
      ) : null}

      {platforms.length ? (
        <LiveNumbersChart
          slotId={slotId}
          platforms={platforms}
          timeZone={timeZone}
          kind={kind}
          granularity={granularity}
          onGranularity={setGranularity}
          height={phone ? 294 : 254}
        />
      ) : null}

      <p className="text-[12px] leading-[16.8px] text-co-muted" aria-live="polite">
        Read hourly for a day from the first read, then daily to day 7
        {source ? ` · ${source}` : ""}
        {updated ? ` · ${updated}` : ""}
        {onRefresh ? (
          <>
            {" · "}
            <button
              type="button"
              onClick={onRefresh}
              disabled={reading}
              className="text-co-text2 underline-offset-4 outline-none hover:text-co-text hover:underline focus-visible:underline disabled:no-underline disabled:opacity-60"
            >
              {reading ? "Reading the numbers…" : "Refresh"}
            </button>
          </>
        ) : null}
      </p>
    </div>
  );

  // The switch, with where the numbers are beside it (under it on the phone).
  const switchRow = comments ? (
    <div className={cn("flex", phone ? "flex-col items-start gap-5" : "flex-wrap items-center gap-x-5 gap-y-2")}>
      <KitSegmentedControl
        variant="lens"
        ariaLabel="Numbers or comments"
        className="h-10"
        value={tab}
        onChange={setTab}
        options={[
          { label: "Numbers", value: "numbers" },
          { label: "Comments", value: "comments" },
        ]}
      />
      {!phone && tab === "numbers" ? <p className="text-[13px] leading-[18px] text-co-muted">{words.line}</p> : null}
    </div>
  ) : null;
  const phoneLabel =
    phone && tab === "numbers" ? (
      <p className="font-section text-[14px] font-semibold uppercase leading-[16.8px] tracking-[0.04em] text-co-muted">{words.label}</p>
    ) : null;

  return (
    <section aria-label="The numbers" className="@container flex min-w-0 flex-col gap-5">
      {switchRow}
      {phoneLabel}
      {tab === "comments" && comments ? comments : block}
    </section>
  );
}
